import { ActionType } from 'typesafe-actions';
import { v4 as uuidv4 } from 'uuid';

import { KnmStatusesEnum } from 'KnmPackage/MainModule/enums/KnmStatusesEnum';
import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';

import * as actions from '../actions/decisionActions';
import { DecisionActionsEnum } from '../enums/DecisionActionsEnum';
import { IDecisionState } from '../interfaces/IDecisionState';
import { IDecisionFormReason } from '../interfaces/IDecisionFormReason';

export type DecisionAction = ActionType<typeof actions>;

const addingReason: IDecisionFormReason = {
  reason: undefined,
  type: undefined,
  text: undefined,
  date: undefined,
  riskIndicators: undefined,
  requirementDetails: undefined,
  dateRequirement: undefined,
  assignmentNumber: undefined,
  assignmentDate: undefined,
  numGuid: undefined,
  dateCreate: undefined,
  dateBegin: undefined,
  dateEnd: undefined,
  order: undefined,
  ervkUuid: undefined,
  links: undefined,
  listId: uuidv4(),
  fullName: undefined,
  indicatorRiskAttachment: undefined,
  indicatorRiskAuto: undefined,
  lastConductedKnmDate: undefined,
  prescriptionDate: undefined,
  prescriptionNumber: undefined,
  programAttachment: undefined,
  programNumber: undefined,
  prosecutorAttachment: undefined,
};

const initialState: IDecisionState = {
  initialFormValues: {
    reasons: [addingReason],
  },
  decisionLoading: true,
  wasFirstInit: false,
  externalParams: {},
  isPrescriptionCheckByOldRules: undefined,
  isPassportByCheckPrescriptionFailed: undefined,
};

export function decisionReducer(state: IDecisionState = initialState, action: DecisionAction): IDecisionState {
  switch (action.type) {
    case DecisionActionsEnum.DECISION_RESET: {
      return initialState;
    }
    case DecisionActionsEnum.DECISION_SET_ACTIONS_DROPDOWN_VISIBLE: {
      return {
        ...state,
        actionsDropdownVisible: action.payload.visible,
      };
    }
    case DecisionActionsEnum.DECISION_PATCH_RAW_DATA: {
      const { partRawData } = action.payload;

      return {
        ...state,
        rawData: state.rawData
          ? {
              ...state.rawData,
              ...partRawData,
            }
          : (partRawData as IDecision),
      };
    }
    case DecisionActionsEnum.DECISION_SET_URL_ID: {
      return {
        ...state,
        urlId: action.payload.urlId,
      };
    }
    case DecisionActionsEnum.DECISION_SET_INITIAL_FORM_VALUES: {
      const { initialFormValues } = action.payload;

      return {
        ...state,
        initialFormValues: {
          ...state.initialFormValues,
          ...initialFormValues,
        },
      };
    }
    case DecisionActionsEnum.DECISION_SET_PRESCRIPTION_OLD_RULES: {
      return { ...state, isPrescriptionCheckByOldRules: action.payload.value };
    }
    case DecisionActionsEnum.DECISION_SET_PRESCRIPTION_FAILED: {
      return { ...state, isPassportByCheckPrescriptionFailed: action.payload.value };
    }
    case DecisionActionsEnum.DECISION_RESET_INITIAL_FORM_VALUES: {
      return {
        ...state,
        initialFormValues: initialState.initialFormValues,
        isPrescriptionCheckByOldRules: undefined,
        isPassportByCheckPrescriptionFailed: undefined,
      };
    }
    case DecisionActionsEnum.DECISION_SET_RAW_DATA: {
      return {
        ...state,
        rawData: action.payload.rawData,
      };
    }
    case DecisionActionsEnum.DECISION_ADD_VISIBLE_MODALS: {
      return {
        ...state,
        decisionVisibleModals: state?.decisionVisibleModals
          ? [...state.decisionVisibleModals, action.payload.value]
          : [action.payload.value],
        modalParams: action.payload.params,
      };
    }
    case DecisionActionsEnum.DECISION_REMOVE_VISIBLE_MODALS: {
      return {
        ...state,
        decisionVisibleModals: state?.decisionVisibleModals?.filter(item => item !== action.payload.value),
      };
    }
    case DecisionActionsEnum.DECISION_GET_REQUEST: {
      return {
        ...state,
        getDecision: {
          ...state?.getDecision,
          status: KnmStatusesEnum.LOADING,
        },
      };
    }
    case DecisionActionsEnum.DECISION_GET_SUCCESS: {
      return {
        ...state,
        rawData: action.payload.data,
        getDecision: {
          ...state?.getDecision,
          status: KnmStatusesEnum.SUCCEEDED,
        },
      };
    }
    case DecisionActionsEnum.DECISION_GET_FAILURE: {
      return {
        ...state,
        getDecision: {
          ...state?.getDecision,
          status: KnmStatusesEnum.FAILED,
        },
      };
    }
    case DecisionActionsEnum.DECISION_UPDATE_REQUEST: {
      return {
        ...state,
        updateDecision: {
          ...state?.updateDecision,
          status: KnmStatusesEnum.LOADING,
        },
      };
    }
    case DecisionActionsEnum.DECISION_UPDATE_SUCCESS: {
      return {
        ...state,
        rawData: action.payload.data,
        updateDecision: {
          ...state?.updateDecision,
          status: KnmStatusesEnum.SUCCEEDED,
        },
      };
    }
    case DecisionActionsEnum.DECISION_UPDATE_FAILURE: {
      return {
        ...state,
        updateDecision: {
          ...state?.updateDecision,
          status: KnmStatusesEnum.FAILED,
        },
      };
    }
    case DecisionActionsEnum.DECISION_SET_FORM_REF: {
      return {
        ...state,
        formRef: action.payload.formRef,
      };
    }
    case DecisionActionsEnum.DECISION_SET_HISTORY_CHANGES_IS_ON: {
      return {
        ...state,
        decisionHistoryChangesIsOn: action.payload.value,
      };
    }
    case DecisionActionsEnum.DECISION_SET_HISTORY_CHANGES_IS_LOADING: {
      return {
        ...state,
        decisionHistoryChangesIsLoading: action.payload.value,
      };
    }
    case DecisionActionsEnum.DECISION_SET_HISTORY_CHANGES: {
      return {
        ...state,
        decisionHistoryChanges: action.payload.data,
      };
    }
    case DecisionActionsEnum.DECISION_SET_SIGNED_HISTORY_CHANGES: {
      return {
        ...state,
        decisionSignedHistoryChanges: action.payload.data,
      };
    }
    case DecisionActionsEnum.DECISION_SET_ERROR_FIELDS: {
      return {
        ...state,
        errorFields: action.payload.errorFields,
      };
    }
    case DecisionActionsEnum.DECISION_SET_ON_SAVE_BUTTON_CLICK: {
      return {
        ...state,
        onSaveButtonClick: action.payload.onSaveButtonClick,
      };
    }
    case DecisionActionsEnum.DECISION_SET_KNO_ORGANIZATION_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          knoOrganization: action.payload.knoOrganization,
        },
      };
    }
    case DecisionActionsEnum.DECISION_SET_REASONS_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          reasons: action.payload.reasons,
        },
      };
    }
    case DecisionActionsEnum.DECISION_SET_SUPERVISION_TYPE_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          supervisionType: action.payload.supervisionType,
        },
      };
    }
    case DecisionActionsEnum.DECISION_SET_KIND_VERSION_ID_FORM_VALUE: {
      return {
        ...state,
        formValues: {
          ...state?.formValues,
          kindVersionId: action.payload.kindVersionId,
        },
      };
    }
    case DecisionActionsEnum.DECISION_SET_STATUS: {
      return {
        ...state,
        status: action.payload.status,
      };
    }
    case DecisionActionsEnum.DECISION_SET_IS_CAN_BE_SIGNED: {
      return {
        ...state,
        isDecisionCanBeSigned: action.payload.data,
      };
    }
    case DecisionActionsEnum.DECISION_SET_ORDERS: {
      return {
        ...state,
        decisionOrdersSelectOptions: action.payload.knmOrders,
        decisionOrderConditionId: action.payload.conditionId,
      };
    }
    case DecisionActionsEnum.DECISION_SET_LOADING: {
      return {
        ...state,
        decisionLoading: action.payload.loading,
      };
    }
    case DecisionActionsEnum.DECISION_SET_STATUS_HINT_TEXT: {
      return {
        ...state,
        statusHintText: action.payload.value,
      };
    }
    case DecisionActionsEnum.DECISION_SET_STATUS_HINT_FIELDS: {
      return {
        ...state,
        statusHintFields: action.payload.fields,
      };
    }
    case DecisionActionsEnum.DECISION_SET_PRESCRIBE_SIGNED: {
      const { isSignComplete } = action.payload;

      return {
        ...state,
        isCancelSignCompleted: isSignComplete,
      };
    }

    case DecisionActionsEnum.DECISION_SET_EXTERNAL_PARAMS: {
      return {
        ...state,
        externalParams: action.payload.value,
      };
    }

    default:
      return state;
  }
}
